import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { GitCompareArrows, SlidersHorizontal, Download, Ghost, Coins, Crown, Lock, Plus, Loader2 } from "lucide-react";
import { useRef } from "react";
import { InputPanel } from "@/components/InputPanel";
import { ReportTable } from "@/components/ReportTable";
import { CompareModal } from "@/components/CompareModal";
import { FilterPopover } from "@/components/FilterPopover";
import { PricingSection } from "@/components/PricingSection";
import { LoginModal } from "@/components/LoginModal";
import { TopupModal } from "@/components/TopupModal";
import { parseDomains, exportCSV } from "@/lib/seo";
import { getClientId } from "@/lib/client";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FILTERS = { da: { min: "", max: "" }, pa: { min: "", max: "" }, ss: { min: "", max: "" }, backlinks: { min: "", max: "" } };

export default function Home() {
  const [mode, setMode] = useState("single");
  const [text, setText] = useState("");
  const [excludeDupes, setExcludeDupes] = useState(false);
  const [excludeSameDomain, setExcludeSameDomain] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasScanned, setHasScanned] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [clientStatus, setClientStatus] = useState(null);
  const location = useLocation();
  const [googleUser, setGoogleUser] = useState(location.state?.googleUser || null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [scanProgress, setScanProgress] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const pendingScanRef = useRef(false);
  const googleUserRef = useRef(googleUser);
  googleUserRef.current = googleUser;

  useEffect(() => {
    axios.get(`${API}/client/${getClientId()}`).then(({ data }) => setClientStatus(data)).catch(() => {});
    if (!location.state?.googleUser) {
      axios.get(`${API}/auth/google/me`, { withCredentials: true })
        .then(({ data }) => setGoogleUser(data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a successful Google login, auto-resume the pending bulk scan.
  useEffect(() => {
    if (googleUser && pendingScanRef.current) {
      pendingScanRef.current = false;
      setLoginOpen(false);
      handleCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleUser]);

  const isPro = clientStatus?.is_pro;
  const showTurnstile = clientStatus ? !clientStatus.turnstile_verified : false;

  const urlCount = useMemo(() => text.split("\n").map((l) => l.trim()).filter(Boolean).length, [text]);

  const handleCheck = async () => {
    const domains = parseDomains(text, { excludeDupes, excludeSameDomain });
    if (domains.length === 0) {
      toast.error("Please enter at least one valid domain.");
      return;
    }
    if (mode === "single" && domains.length > 1) {
      toast.error("Single mode accepts only one domain. Switch to Bulk mode for multiple domains.");
      return;
    }
    if (domains.length > 1000) {
      toast.error("Maximum 1000 domains per scan.");
      return;
    }
    setLoading(true);
    setHasScanned(true);
    setResults([]);
    setScanProgress({ done: 0, total: domains.length });
    try {
      const resp = await fetch(`${API}/check-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ domains, mode, client_id: getClientId(), turnstile_token: turnstileToken || undefined }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw { response: { status: resp.status, data: err } };
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let collected = [];
      let finalPayload = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const ev = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.type === "batch") {
            collected = [...collected, ...payload.results];
            setResults(collected);
            setScanProgress({ done: payload.done, total: payload.total });
          } else if (payload.type === "done") {
            finalPayload = payload;
          }
        }
      }
      setTurnstileToken("");
      if (finalPayload?.client) setClientStatus(finalPayload.client);
      const okCount = finalPayload?.ok_count ?? collected.filter((r) => r.status !== "Error").length;
      toast.success(`Scan complete — ${okCount}/${domains.length} domains resolved in ${((finalPayload?.duration_ms || 0) / 1000).toFixed(1)}s`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 426) {
        pendingScanRef.current = true;
        setLoginOpen(true);
        setTurnstileToken("");
        setTurnstileKey((k) => k + 1);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Scan failed. The API may be unavailable — please try again.");
      if (e.response?.status === 402) setTopupOpen(true);
      setTurnstileToken("");
      setTurnstileKey((k) => k + 1);
      axios.get(`${API}/client/${getClientId()}`).then(({ data }) => setClientStatus(data)).catch(() => {});
    } finally {
      setLoading(false);
      setScanProgress(null);
    }
  };

  const activeFilterCount = useMemo(
    () => Object.values(filters).reduce((acc, r) => acc + (r.min !== "" ? 1 : 0) + (r.max !== "" ? 1 : 0), 0),
    [filters]
  );

  const displayRows = useMemo(() => {
    let rows = results.filter((r) => {
      for (const key of Object.keys(filters)) {
        const { min, max } = filters[key];
        const v = r[key];
        if (min !== "" && (v === null || v === undefined || v < Number(min))) return false;
        if (max !== "" && (v === null || v === undefined || v > Number(max))) return false;
      }
      return true;
    });
    if (sortKey) {
      rows = [...rows].sort((x, y) => {
        const a = x[sortKey], b = y[sortKey];
        if (a === b) return 0;
        if (a === null || a === undefined) return 1;
        if (b === null || b === undefined) return -1;
        const cmp = typeof a === "string" ? a.localeCompare(b) : a - b;
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [results, filters, sortKey, sortDir]);

  const handleHeaderSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const handleExport = () => {
    if (displayRows.length === 0) {
      toast.error("Nothing to export — run a scan first.");
      return;
    }
    exportCSV(displayRows);
    toast.success("CSV exported.");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-gray-900">
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <header className="w-full py-14 flex flex-col items-center fade-up fade-up-1">
          <div className="flex items-center gap-3 mb-2">
            <Ghost size={40} className="text-[#C0392B]" strokeWidth={1.75} />
            <h1
              data-testid="page-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 text-center tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              DA PA Checker
            </h1>
          </div>
          <p data-testid="page-subtitle" className="text-base md:text-lg text-gray-600 text-center max-w-2xl">
            Free bulk <strong>DA PA checker</strong> — check Domain Authority and Page Authority for up to 1000 domains at once. Compare, filter &amp; export DA/PA scores to CSV.
          </p>
          {clientStatus && (
            <div className="mt-5 inline-flex items-center gap-4 px-5 py-2 bg-white border border-gray-200 rounded-full shadow-sm text-sm" data-testid="credits-bar">
              <span className="flex items-center gap-1.5 text-gray-700">
                <Coins size={15} className="text-amber-600" />
                <span data-testid="free-credits-left" className="font-semibold">{clientStatus.free_credits_left}</span>
                <span className="text-gray-400">/ {clientStatus.daily_limit} free today</span>
              </span>
              <span className="w-px h-4 bg-gray-200" />
              <span className="text-gray-700">
                <span data-testid="purchased-credits" className="font-semibold">{clientStatus.purchased_credits}</span>
                <span className="text-gray-400"> purchased</span>
              </span>
              <button
                data-testid="topup-credits-btn"
                onClick={() => setTopupOpen(true)}
                title="Top up credits"
                className="w-6 h-6 rounded-full bg-[#C0392B] hover:bg-[#A93226] text-white flex items-center justify-center transition-colors -mr-1"
              >
                <Plus size={14} />
              </button>
              {isPro && (
                <>
                  <span className="w-px h-4 bg-gray-200" />
                  <span className="flex items-center gap-1 text-[#C0392B] font-bold" data-testid="pro-badge">
                    <Crown size={14} /> PRO
                  </span>
                </>
              )}
            </div>
          )}
        </header>

        <InputPanel
          mode={mode}
          setMode={setMode}
          text={text}
          setText={setText}
          urlCount={urlCount}
          excludeDupes={excludeDupes}
          setExcludeDupes={setExcludeDupes}
          excludeSameDomain={excludeSameDomain}
          setExcludeSameDomain={setExcludeSameDomain}
          onCheck={handleCheck}
          loading={loading}
          showTurnstile={showTurnstile}
          turnstileToken={turnstileToken}
          onTurnstileToken={setTurnstileToken}
          turnstileKey={turnstileKey}
        />

        <section className="w-full mt-16 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden fade-up fade-up-3" data-testid="report-section">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-6 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="report-heading">
              Report
            </h2>
            {scanProgress && (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#C0392B]" data-testid="scan-progress">
                <Loader2 size={14} className="animate-spin" /> Scanning {scanProgress.done}/{scanProgress.total}…
              </span>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="compare-btn"
                onClick={() => {
                  if (results.length < 2) {
                    toast.error("Scan at least 2 domains to use Compare.");
                    return;
                  }
                  setCompareOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:border-[#C0392B] hover:text-[#C0392B] transition-colors"
              >
                <GitCompareArrows size={15} /> Compare
                <span className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">NEW</span>
              </button>
              {isPro ? (
                <FilterPopover
                  filters={filters}
                  setFilters={setFilters}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  setSortKey={setSortKey}
                  setSortDir={setSortDir}
                  activeCount={activeFilterCount}
                >
                  <button
                    data-testid="filter-btn"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                      activeFilterCount > 0 || sortKey
                        ? "border-[#C0392B] text-[#C0392B] bg-[#FADBD8]/40"
                        : "border-gray-200 bg-white text-gray-700 hover:border-[#C0392B] hover:text-[#C0392B]"
                    }`}
                  >
                    <SlidersHorizontal size={15} /> Filter
                    <span className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">PRO</span>
                  </button>
                </FilterPopover>
              ) : (
                <button
                  data-testid="filter-btn"
                  onClick={() => {
                    toast.error("Filter is a PRO feature — upgrade for $10 to unlock it.");
                    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:border-amber-500 hover:text-amber-700 transition-colors"
                >
                  <Lock size={14} /> Filter
                  <span className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">PRO</span>
                </button>
              )}
              <button
                data-testid="export-btn"
                onClick={handleExport}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:border-[#C0392B] hover:text-[#C0392B] transition-colors"
              >
                <Download size={15} /> Export
              </button>
            </div>
          </div>

          <ReportTable
            rows={displayRows}
            loading={loading}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleHeaderSort}
            hasScanned={hasScanned && results.length > 0}
          />
        </section>

        <PricingSection clientStatus={clientStatus} onStatusChange={setClientStatus} googleUser={googleUser} />

        <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />

        <section className="w-full mt-24 max-w-4xl mx-auto fade-up fade-up-6" data-testid="seo-content">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Free DA PA Checker for Bulk Domain Analysis
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Wraith Checker is a free <strong>DA PA checker</strong> that reports Domain Authority (DA) and Page Authority (PA) for any website. Paste up to 1000 domains, hit Check, and get a sortable, exportable DA/PA report in seconds — no signup, no API key, no credit card.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Bulk DA PA Scan</h3>
              <p className="text-sm text-gray-600">Check Domain Authority and Page Authority for up to 1000 domains in one request. Deduplicate, exclude same-root domains, and scan single or bulk mode.</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Compare &amp; Filter</h3>
              <p className="text-sm text-gray-600">Compare Site A vs Site B metric-by-metric with a weighted verdict. PRO filter lets you sort by DA, PA, Spam Score, and backlinks with custom min/max ranges.</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>CSV Export</h3>
              <p className="text-sm text-gray-600">Export the filtered and sorted DA PA report as a CSV file. 14 columns including DA, PA, spam score, backlinks, status, and HTTP code for your link-building pipeline.</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              DA PA Checker — Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">What is a DA PA checker?</h3>
                <p className="text-sm text-gray-600">A DA PA checker is an SEO tool that reports the Domain Authority (DA) and Page Authority (PA) scores for a website. DA measures the overall ranking strength of a domain, while PA estimates the authority of a specific page. Wraith Checker lets you check DA and PA for up to 1000 domains in bulk.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Is this DA PA checker free?</h3>
                <p className="text-sm text-gray-600">Yes. Wraith Checker gives you 25 free domain checks every day with no signup required. For heavier use, a one-time PRO upgrade unlocks 500 checks per day plus the advanced filter feature.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">How many domains can I check at once?</h3>
                <p className="text-sm text-gray-600">You can check up to 1000 domains in a single bulk scan. Paste your list into the input, click Check, and download the DA PA results as a CSV file.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">What do DA and PA mean?</h3>
                <p className="text-sm text-gray-600">DA (Domain Authority) is a 0-100 score predicting how well a domain will rank on search engines. PA (Page Authority) is the same prediction for a single page. Higher scores mean stronger ranking potential.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Can I export DA PA results to CSV?</h3>
                <p className="text-sm text-gray-600">Yes. After scanning, click Export to download the filtered and sorted DA PA report as a CSV file with 14 columns of metrics.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <CompareModal open={compareOpen} onOpenChange={setCompareOpen} results={results} />
      <TopupModal open={topupOpen} onOpenChange={setTopupOpen} onStatusChange={setClientStatus} />
    </div>
  );
}
